/**
 * Pure map-projection math (no Babylon): v6 `View.w2s` / `s2w`, fitted presets (09 §5, §12) and
 * crisp zoom levels. Shared by `MapCamera`, the inset cameras, the controller and the unit tests.
 *
 * Convention (v6 `View`): screen = centerPx + R(rot)·(world − center) / mpp with screen y down,
 * where R(rot) rotates counter-clockwise. All screen values are **stage px**; mpp is metres per
 * stage px. Device mpp = mpp / k (k = u in native mode).
 */
import type { Rect, ViewPreset } from '../api.ts';
import type { ViewPreset as ManifestViewPreset } from '../bundle/manifest.gen.ts';

/** Zoom clamp in metres per stage px (03 §5): the same feel at every `u`. */
export const MPP_MIN = 0.25;
export const MPP_MAX = 4;
/** The portrait fitted view's zoom-out clamp (portrait plan §V: the whole track on a phone is
 *  2–7 m/px; tiles minify from z0). Landscape keeps `MPP_MAX`. */
export const MPP_MAX_PORTRAIT = 7;
/** Crisp multipliers of a tile level's mpp (03 §5). */
export const CRISP_MULTIPLIERS: readonly number[] = [1, 1.5, 2, 3];
/** `crispAtOrAbove` snaps down when that crops at most this fraction of the frame (09 §12). */
export const SNAP_DOWN_CROP = 0.02;
/** Default tile level mpps (Monza tiles.json z0…z4); callers pass the bundle's levels when they have them. */
export const DEFAULT_TILE_MPPS: readonly number[] = [4, 2, 1, 0.5, 0.25];

type Out2 = Float32Array | Float64Array | number[];

/** Minimal view state for projection: everything `w2s` needs. */
export interface ViewLike {
  readonly cx: number;
  readonly cy: number;
  readonly mpp: number;
  /** cos / sin of rotDeg (cached by the owner) */
  readonly cos: number;
  readonly sin: number;
  readonly cpx: number;
  readonly cpy: number;
}

/** World → stage px, exactly v6 `View.w2s` (double precision). */
export function w2s(v: ViewLike, x: number, y: number, out: Out2): void {
  const dx = x - v.cx;
  const dy = y - v.cy;
  const rx = dx * v.cos - dy * v.sin;
  const ry = dx * v.sin + dy * v.cos;
  out[0] = v.cpx + rx / v.mpp;
  out[1] = v.cpy - ry / v.mpp;
}

/** Stage px → world, exactly v6 `View.s2w`. */
export function s2w(v: ViewLike, px: number, py: number, out: Out2): void {
  const rx = (px - v.cpx) * v.mpp;
  const ry = -(py - v.cpy) * v.mpp;
  out[0] = rx * v.cos + ry * v.sin + v.cx;
  out[1] = -rx * v.sin + ry * v.cos + v.cy;
}

/** v6 `View.heading_to_screen`: world heading (rad, CCW from +x) → screen angle (x right, y down). */
export function headingToScreen(headingRad: number, rotDeg: number): number {
  return -(headingRad + (rotDeg * Math.PI) / 180);
}

/** Manifest `views[...]` (v6 reference preset, map_rect as [x0,y0,x1,y1]) → core `ViewPreset`. */
export function presetFromManifest(v: ManifestViewPreset): ViewPreset {
  const [x0, y0, x1, y1] = v.map_rect;
  return {
    center: [v.center[0], v.center[1]],
    mpp: v.mpp,
    rotDeg: v.rot_deg,
    centerPx: [v.center_px[0], v.center_px[1]],
    mapRect: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
  };
}

// ───────────────────────────── crisp levels (03 §5, 09 §5) ─────────────────────────────

/**
 * Crisp zoom levels in **stage** mpp, sorted ascending and de-duplicated:
 * `k · tileMpp · {1, 1.5, 2, 3}` (device mpp equal to a tile level × multiplier) plus `extra`
 * (the style's reference preset mpp and the live fitted preset mpp, already in stage mpp).
 */
export function crispLevels(tileMpps: readonly number[], k: number, extra: readonly number[] = []): number[] {
  const out: number[] = [];
  for (const t of tileMpps) for (const m of CRISP_MULTIPLIERS) out.push(t * m * k);
  for (const e of extra) if (Number.isFinite(e) && e > 0) out.push(e);
  out.sort((a, b) => a - b);
  const uniq: number[] = [];
  for (const v of out) {
    const last = uniq[uniq.length - 1];
    if (last === undefined || Math.abs(v - last) > 1e-9 * v) uniq.push(v);
  }
  return uniq;
}

/**
 * The smallest crisp level ≥ `mpp` (never cut the frame), except that it snaps **down** to the
 * next level below when that crops at most `cropTol` of the frame (09 §12: QHD and 21:9 actually
 * zoom in). The crop of a level `L < mpp` along the binding axis is `1 − L / mpp`.
 * Above every level, `mpp` is returned unchanged.
 */
export function crispAtOrAbove(mpp: number, levels: readonly number[], cropTol = SNAP_DOWN_CROP): number {
  let above = Number.NaN;
  let below = Number.NaN;
  for (const L of levels) {
    if (L >= mpp * (1 - 1e-12)) {
      above = L;
      break;
    }
    below = L;
  }
  if (!Number.isNaN(below) && 1 - below / mpp <= cropTol + 1e-12) return below;
  return Number.isNaN(above) ? mpp : above;
}

/** Index of the crisp level nearest to `mpp` in log space (-1 for an empty list). */
export function nearestCrispIndex(mpp: number, levels: readonly number[]): number {
  let best = -1;
  let bestD = Infinity;
  const lm = Math.log(mpp);
  for (let i = 0; i < levels.length; i++) {
    const d = Math.abs(Math.log(levels[i] ?? 1) - lm);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Idle snap target after a **zoom** gesture (03 §5). A gesture smaller than half a wheel notch
 * (|log(mpp / start)| < ½·log 1.15, e.g. a trackpad nudge) returns to the start level. Otherwise
 * the nearest crisp level, except that a gesture is never undone: when the nearest level is the
 * start level, take its neighbour in the gesture's direction (one notch from 1.5 goes to 1.0).
 * The start level is the crisp level nearest to the gesture's start mpp.
 */
export function idleCrispTarget(mpp: number, mppGestureStart: number, levels: readonly number[]): number {
  const i = nearestCrispIndex(mpp, levels);
  if (i < 0) return mpp;
  const startI = nearestCrispIndex(mppGestureStart, levels);
  const lr = Math.log(mpp / mppGestureStart);
  if (Math.abs(lr) < 0.5 * Math.log(1.15)) return levels[startI] ?? mpp;
  let j = i;
  if (i === startI) {
    j = lr < 0 ? i - 1 : i + 1;
    if (j < 0 || j >= levels.length) j = i;
  }
  return levels[j] ?? mpp;
}

// ───────────────────────────── fitted preset (09 §5, §12) ─────────────────────────────

export interface FitOptions {
  /** device px per stage px (u, or f in fit mode). Default 1. */
  k?: number;
  /** tile level mpps (device mpp). Default: Monza z0…z4. */
  tileMpps?: readonly number[];
  /**
   * The rect the fitted preset frames in this layout (09 §12 `ViewPreset.frameRect`).
   * Default: `mapRect`. The reference frame is `ref.frameRect ?? ref.mapRect`.
   */
  frameRect?: Rect;
  /** Override `ref.crisp` (default true): false keeps `mpp₀ · k` exactly (vector-only styles). */
  crisp?: boolean;
  /** Snap-down crop tolerance. Default 0.02. */
  cropTol?: number;
}

/** Reference frame scale factor: fit the whole reference frame into `frame` (09 §5). */
export function fitScale(refFrame: Rect, frame: Rect): number {
  return Math.max(refFrame.w / frame.w, refFrame.h / frame.h);
}

/**
 * Fit a reference preset to a layout (09 §5):
 * ```
 * k        = max(frame₀.w / R.w, frame₀.h / R.h)
 * mpp      = crispAtOrAbove(mpp₀ · k)            (unless crisp: false)
 * centerPx = R.centre + (centerPx₀ − frame₀.centre) · (mpp₀ / mpp), rounded to whole stage px
 * ```
 * At the reference layout (R = frame₀) this returns the reference preset exactly.
 */
export function fitPreset(ref: ViewPreset, mapRect: Rect, opts: FitOptions = {}): ViewPreset {
  const k = opts.k ?? 1;
  const frame0 = ref.frameRect ?? ref.mapRect;
  const R = opts.frameRect ?? mapRect;
  const scale = fitScale(frame0, R);
  const crisp = opts.crisp ?? ref.crisp ?? true;
  const raw = ref.mpp * scale;
  let mpp = raw;
  if (crisp) {
    const levels = crispLevels(opts.tileMpps ?? DEFAULT_TILE_MPPS, k, [ref.mpp]);
    mpp = crispAtOrAbove(raw, levels, opts.cropTol ?? SNAP_DOWN_CROP);
  }
  if (Math.abs(mpp - ref.mpp) < 1e-12) mpp = ref.mpp;
  const f0cx = frame0.x + frame0.w / 2;
  const f0cy = frame0.y + frame0.h / 2;
  const rcx = R.x + R.w / 2;
  const rcy = R.y + R.h / 2;
  const s = ref.mpp / mpp;
  const out: ViewPreset = {
    center: [ref.center[0], ref.center[1]],
    mpp,
    rotDeg: ref.rotDeg,
    centerPx: [Math.round(rcx + (ref.centerPx[0] - f0cx) * s), Math.round(rcy + (ref.centerPx[1] - f0cy) * s)],
    mapRect: { ...mapRect },
  };
  if (opts.frameRect) out.frameRect = { ...opts.frameRect };
  if (ref.crisp !== undefined) out.crisp = ref.crisp;
  return out;
}

const cornerTmp = [0, 0];

/**
 * The world-space corners of a stage rect under a view, in order TL, TR, BR, BL (into `out`, 8 values).
 * Allocation-free.
 */
export function rectCornersWorld(v: ViewLike, r: Rect, out: Float64Array | number[]): void {
  for (let i = 0; i < 4; i++) {
    const px = i === 1 || i === 2 ? r.x + r.w : r.x;
    const py = i >= 2 ? r.y + r.h : r.y;
    s2w(v, px, py, cornerTmp);
    out[i * 2] = cornerTmp[0] ?? 0;
    out[i * 2 + 1] = cornerTmp[1] ?? 0;
  }
}

// ───────────────────────────── viewport conversion (09 §6) ─────────────────────────────

/** Integer backbuffer-px rect (origin top-left) for a stage rect at `pxScale` backbuffer px per stage px. */
export interface PxRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function stageRectToBackbuffer(r: Rect, pxScale: number, out: PxRect): PxRect {
  out.x0 = Math.round(r.x * pxScale);
  out.y0 = Math.round(r.y * pxScale);
  out.x1 = Math.round((r.x + r.w) * pxScale);
  out.y1 = Math.round((r.y + r.h) * pxScale);
  return out;
}

export interface NormViewport {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Babylon normalized viewport (origin bottom-left) for an integer backbuffer rect. A quarter-pixel
 * bias makes Babylon's `x * width` truncate to exactly the intended integer.
 */
export function normalizedViewport(px: PxRect, bbW: number, bbH: number, out: NormViewport): NormViewport {
  const w = Math.max(0, px.x1 - px.x0);
  const h = Math.max(0, px.y1 - px.y0);
  out.x = (px.x0 + 0.25) / bbW;
  out.y = (bbH - px.y1 + 0.25) / bbH;
  out.w = (w + 0.25) / bbW;
  out.h = (h + 0.25) / bbH;
  return out;
}

export interface OrthoBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Orthographic bounds (world metres in camera space) so that the camera eye (world `center`)
 * lands on `centerPx` inside the viewport whose stage-px extent is [sx0, sx1] × [sy0, sy1].
 */
export function orthoBounds(
  mpp: number,
  cpx: number,
  cpy: number,
  sx0: number,
  sy0: number,
  sx1: number,
  sy1: number,
  out: OrthoBounds,
): OrthoBounds {
  out.left = -(cpx - sx0) * mpp;
  out.right = (sx1 - cpx) * mpp;
  out.top = (cpy - sy0) * mpp;
  out.bottom = -(sy1 - cpy) * mpp;
  return out;
}
